/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QrReader } from 'react-qr-reader';

import Swal from 'sweetalert2';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { KrTicket, KrBoxes } from '~/components/KoroIcons';
import { Container } from './styles';
import ButtonBack from '~/components/ButtonBack';
import api from '~/services/api';
import { useAuth } from '~/hooks/Auth';
import Modal from './Modal';

type KitInfo = {
  [key: string]: string;
};

type ParsedResult = {
  [key: string]: string | number | KitInfo | undefined;
};

type ScannerMode = 'camera' | 'collector';

type LastValidated = {
  name: string;
  modalidade?: string;
  categoria?: string;
  kit?: KitInfo;
  at: number;
};

const QrCode: React.FC = () => {
  const successRef = useRef<HTMLAudioElement>(null);
  const errorRef = useRef<HTMLAudioElement>(null);
  const collectorInputRef = useRef<HTMLTextAreaElement>(null);
  const { event } = useAuth();
  const [readySuccess, setReadySuccess] = useState(false);
  const [readyError, setReadyError] = useState(false);
  const [mode, setMode] = useState<ScannerMode | null>(null);
  const [collectorBuffer, setCollectorBuffer] = useState('');
  const [validatedCount, setValidatedCount] = useState(0);
  const [lastValidated, setLastValidated] = useState<LastValidated | null>(
    null
  );
  const [pulse, setPulse] = useState<'idle' | 'success' | 'error'>('idle');
  const [manualOpen, setManualOpen] = useState(false);
  const [isReading, setIsReading] = useState(true);

  const focusCollectorInput = useCallback(() => {
    setTimeout(() => {
      collectorInputRef.current?.focus();
    }, 50);
  }, []);

  const askScannerMode = useCallback(() => {
    Swal.fire({
      title: '<span style="color: #fff;">Como deseja escanear?</span>',
      html: '<span style="color: #f8f9fa;">Escolha o método de leitura do QR Code</span>',
      background: '#212529',
      iconColor: '#fff',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: 'Usar coletor',
      cancelButtonText: 'Usar câmera',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#0d6efd',
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setMode('collector');
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        setMode('camera');
      }
    });
  }, []);

  const handleExecuteAudio = useCallback(
    (type: 'success' | 'error') => {
      if (type === 'success') {
        if (readySuccess) {
          successRef.current?.play();
        }
      } else if (readyError) {
        errorRef.current?.play();
      }
    },
    [readyError, readySuccess]
  );

  const insertQrcodeManually = useCallback(() => {
    setManualOpen(true);

    Swal.fire({
      showConfirmButton: false,
      showCancelButton: false,

      allowOutsideClick: () => !Swal.isLoading(),
      html: '<div id="swal-react-container"></div>',
      didOpen: () => {
        setManualOpen(true);
        const container = document.getElementById('swal-react-container');
        if (container) {
          ReactDOM.render(
            <Modal
              eventId={event.id}
              eventCodeId={event.codes[0].id}
              onExecuteAudio={handleExecuteAudio}
            />,
            container
          );
        }
      },
      didClose: () => {
        setManualOpen(false);
        if (mode === 'collector') {
          focusCollectorInput();
        }
      },
    });
  }, [event, handleExecuteAudio, mode, focusCollectorInput]);

  useEffect(() => {
    if (window.ReactNativeWebView) {
      const dataMessage = {
        checkCameraPermission: true,
      };

      window.ReactNativeWebView.postMessage(JSON.stringify(dataMessage));
    }
  }, []);

  useEffect(() => {
    if (mode === null) {
      askScannerMode();
    }
  }, [mode, askScannerMode]);

  useEffect(() => {
    if (mode === 'collector') {
      focusCollectorInput();
    }
  }, [mode, focusCollectorInput]);

  const buildSuccessHtml = useCallback((qrCodeData: ParsedResult): string => {
    const lines: string[] = [];

    const { modalidade, categoria, informacoes_kit: kit } = qrCodeData;

    lines.push(
      `<div style="color:#fff; font-size: 20px; text-align: center;"><b>Ingresso validado com sucesso!</b></div>`
    );

    if (modalidade) {
      lines.push(
        `<div style="color:#fff; font-size: 16px;"><b>Modalidade:</b> ${modalidade}</div>`
      );
    }
    if (categoria) {
      lines.push(
        `<div style="color:#fff; font-size: 16px;"><b>Categoria:</b> ${categoria}</div>`
      );
    }
    if (kit && typeof kit === 'object') {
      const kitEntries = Object.entries(kit as KitInfo)
        .map(
          ([k, v]) =>
            `<div style="color:#fff; font-size: 14px;"><b>${k}:</b> ${v}</div>`
        )
        .join('');
      if (kitEntries) {
        lines.push(
          `<div style="margin-top:10px;"><div style="color:#fff; font-size: 16px;"><b>Kit:</b></div>${kitEntries}</div>`
        );
      }
    }

    return lines.length > 0
      ? `<div style="text-align:left; margin-top: 8px;">${lines.join('')}</div>`
      : '';
  }, []);

  const handleReaderQrCode = useCallback(
    async (qrCode: string) => {
      if (!isReading) return;

      setIsReading(false);

      const element = document.querySelector('.swal2-container');

      if (!element) {
        try {
          const ticket_id =
            qrCode.match(/Identificador:\s*#(\d+)/)?.[1] ?? qrCode.trim();
          // procura o ingresso pelo code
          // const ticket = allTickets.find((item) => item.code === code);

          // if (!ticket) {
          //   throw new Error('Ingresso não encontrado');
          // }

          const formData = {
            eventCode_id: event.codes[0].id,
            ticket_id: parseInt(ticket_id.slice(-5), 10),
            event_id: event.id,
          };

          const response = await api.post('tickets/validate', formData);

          handleExecuteAudio('success');

          setValidatedCount((prev) => prev + 1);

          setLastValidated({
            name: response.data.name,
            modalidade: response.data.modality?.name,
            categoria: response.data.batchCategory?.category?.name,
            kit: response.data.kit ? { tamanho: response.data.kit } : undefined,
            at: Date.now(),
          });

          setPulse('success');
          setTimeout(() => setPulse('idle'), 1500);

          Swal.fire({
            icon: 'success',
            title: `<span style="color:#fff;">${
              response.data.name ?? ''
            }</span>`,
            html: buildSuccessHtml({
              modalidade: response.data.modality?.name,
              categoria: response.data.batchCategory?.category?.name,
              informacoes_kit: response.data.kit
                ? { tamanho: response.data.kit }
                : undefined,
            }),
            background: '#198754',
            iconColor: '#fff',
            timer: 2000,
            didClose: () => {
              setIsReading(true);

              if (mode === 'collector') {
                focusCollectorInput();
              }
            },
          });
        } catch (error: unknown) {
          handleExecuteAudio('error');

          setPulse('error');
          setTimeout(() => setPulse('idle'), 1500);

          const getErrorMessage = (errorSwal: unknown) => {
            if (axios.isAxiosError(errorSwal)) {
              return errorSwal.response?.data?.message ?? 'Tente novamente.';
            }

            if (errorSwal instanceof Error) {
              return errorSwal.message;
            }

            return 'Tente novamente.';
          };

          Swal.fire({
            icon: 'error',
            title: '<span style="color:#fff;">Ops...</span>',
            html: `<span style="color:#f8f9fa;">${getErrorMessage(
              error
            )}</span>`,
            background: '#dc3545',
            iconColor: '#fff',
            timer: 2000,
            didClose: () => {
              setIsReading(true);

              if (mode === 'collector') {
                focusCollectorInput();
              }
            },
          });
        }
      }
    },
    [
      event,
      handleExecuteAudio,
      buildSuccessHtml,
      isReading,
      mode,
      focusCollectorInput,
    ]
  );

  const handleCollectorKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const text = collectorBuffer.trim();
        setCollectorBuffer('');
        if (text) {
          handleReaderQrCode(text.trim());
        }
      }
    },
    [collectorBuffer, handleReaderQrCode]
  );

  return (
    <Container className={mode === 'collector' ? 'collector-mode' : ''}>
      <ButtonBack
        href={`${process.env.PUBLIC_URL}/dashboard`}
        className="btn-back text-white"
      />
      {mode !== 'collector' && (
        <h2 className="text-white px-3">{event.name}</h2>
      )}
      {mode === 'camera' && readySuccess && readyError && (
        <QrReader
          constraints={{ facingMode: 'environment' }}
          onResult={(result) => {
            if (!isReading) return;
            if (result) {
              handleReaderQrCode(result.getText().trim());
            }
          }}
          className="scanner"
        />
      )}
      {mode === 'camera' && (
        <div className="square">
          <div className="left">
            <div />
            <div />
            <div />
          </div>
          <div className="right">
            <div />
            <div />
            <div />
          </div>
        </div>
      )}
      {mode === 'collector' && (
        <div className="collector-screen">
          <textarea
            ref={collectorInputRef}
            value={collectorBuffer}
            onChange={(e) => setCollectorBuffer(e.target.value)}
            onKeyDown={handleCollectorKeyDown}
            onBlur={() => {
              if (!manualOpen) {
                focusCollectorInput();
              }
            }}
            className="collector-input"
            aria-hidden={manualOpen ? 'false' : 'true'}
            tabIndex={-1}
            inputMode="none"
          />

          <header className="collector-header">
            <span className="event-label">Evento</span>
            <h1>{event.name}</h1>
          </header>

          <div className={`collector-radar pulse-${pulse}`}>
            <div className="radar-ring ring-1" />
            <div className="radar-ring ring-2" />
            <div className="radar-ring ring-3" />
            <div className="radar-core">
              <KrTicket color="#fff" />
            </div>
          </div>

          <div className="collector-status">
            <span className="dot" />
            <span className="label">Pronto para ler</span>
          </div>

          <div className="collector-counter">
            <span className="value">
              {validatedCount.toString().padStart(2, '0')}
            </span>
            <span className="caption">leituras nesta sessão</span>
          </div>

          {lastValidated && (
            <div className="collector-last-card">
              <div className="last-header">
                <KrBoxes />
                <span>Última validação</span>
              </div>
              <div className="last-name">{lastValidated.name || '—'}</div>
              <div className="last-tags">
                {lastValidated.modalidade && (
                  <span className="tag">{lastValidated.modalidade}</span>
                )}
                {lastValidated.categoria && (
                  <span className="tag tag-secondary">
                    {lastValidated.categoria}
                  </span>
                )}
              </div>
              {lastValidated.kit && (
                <ul className="last-kit">
                  {Object.entries(lastValidated.kit).map(([k, v]) => (
                    <li key={k}>
                      <b>{k}:</b> {v}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={insertQrcodeManually}
        className="btn-manual btn btn-primary px-4"
      >
        Entrada manual
      </button>
      <audio
        ref={successRef}
        src="https://s3.us-east-1.amazonaws.com/cdn.koro.com.br/sounds/sounds/success-sound.mp3"
        preload="auto"
        onLoadedData={() => {
          setReadySuccess(true);
        }}
      >
        <source
          src="https://s3.us-east-1.amazonaws.com/cdn.koro.com.br/sounds/sounds/success-sound.mp3"
          type="audio/mpeg"
        />
        <track
          kind="captions"
          src="/captions/empty.vtt"
          srcLang="pt"
          label="Português"
          default
        />
        Seu navegador não suporta a audio.
      </audio>
      <audio
        ref={errorRef}
        src="https://s3.us-east-1.amazonaws.com/cdn.koro.com.br/sounds/sounds/error-sound.mp3"
        preload="auto"
        onLoadedData={() => {
          setReadyError(true);
        }}
      >
        <source
          src="https://s3.us-east-1.amazonaws.com/cdn.koro.com.br/sounds/sounds/error-sound.mp3"
          type="audio/mpeg"
        />
        <track
          kind="captions"
          src="/captions/empty.vtt"
          srcLang="pt"
          label="Português"
          default
        />
        Seu navegador não suporta a audio.
      </audio>
    </Container>
  );
};

export default QrCode;
